import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  FileText, 
  FileCheck, 
  Users, 
  Package
} from 'lucide-react';

/**
 * QuickCreateMenu — 2026 Fast Create Dropdown
 * 
 * Inspired by Stripe × Linear × Notion
 * - Lightweight popover anchored to + button
 * - Write-first: immediate action
 * - No search, no modal feel
 * - Background remains interactive
 * - Fast animation (<160ms)
 */
export default function QuickCreateMenu({ 
  isOpen, 
  onClose, 
  onNavigate,
  anchorRef
}) {
  const dropdownRef = useRef(null);

  // Quick create actions
  const actions = [
    {
      id: 'create-invoice',
      label: 'Create Invoice',
      icon: FileText,
      action: () => onNavigate('invoices', { action: 'create' }),
      shortcut: '⌘N',
    },
    {
      id: 'create-quotation',
      label: 'Create Quotation',
      icon: FileCheck,
      action: () => onNavigate('quotations', { action: 'create' }),
    },
    {
      id: 'add-client',
      label: 'Add Client',
      icon: Users,
      action: () => onNavigate('clients', { action: 'create' }),
    },
    {
      id: 'add-product',
      label: 'Add Product',
      icon: Package,
      action: () => onNavigate('products', { action: 'create' }),
    },
  ];

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };

    // Small delay to prevent immediate close on button click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Execute action
  const executeAction = (action) => {
    action.action();
    onClose();
  };

  if (!isOpen) return null;

  // Calculate position
  const rect = anchorRef?.current?.getBoundingClientRect();
  const position = rect ? {
    top: rect.bottom + 8,
    right: window.innerWidth - rect.right,
  } : { top: 60, right: 24 };

  // Render in portal at document root for global click-outside
  return createPortal(
    <>
      {/* Quick Create Dropdown - Anchored to + button */}
      <div 
        ref={dropdownRef}
        className="fixed z-50"
        style={{
          top: `${position.top}px`,
          right: `${position.right}px`,
          width: '240px',
          animation: 'quickCreateSlide 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div 
          className="bg-white rounded-xl overflow-hidden"
          style={{
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.08), 0 8px 24px rgba(15, 23, 42, 0.12), 0 16px 48px rgba(15, 23, 42, 0.08)',
          }}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
              Create
            </p>
          </div>

          {/* Actions List */}
          <div className="py-1">
            {actions.map((action) => {
              const Icon = action.icon;

              return (
                <button
                  key={action.id}
                  onClick={() => executeAction(action)}
                  className="
                    w-full px-3 py-2.5
                    flex items-center gap-3
                    text-left
                    hover:bg-slate-50
                    transition-colors duration-100
                    group
                  "
                >
                  {/* Icon */}
                  <div 
                    className="
                      w-8 h-8 rounded-lg
                      flex items-center justify-center
                      bg-slate-100 text-slate-600
                      group-hover:bg-indigo-50 group-hover:text-indigo-600
                      transition-colors duration-100
                    "
                  >
                    <Icon size={16} />
                  </div>

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {action.label}
                    </div>
                  </div>

                  {/* Shortcut (if any) */}
                  {action.shortcut && (
                    <div className="text-xs text-slate-400 font-mono">
                      {action.shortcut}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes quickCreateSlide {
          0% {
            opacity: 0;
            transform: scale(0.94) translateY(-6px);
          }
          60% {
            opacity: 0.9;
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </>,
    document.body
  );
}
